// /frontend/src/stores/auth.store.ts

/**
 * Pinia store for handling authentication state and actions.
 *
 * @author Alexander Funke
 * @lastChange 2026-10-15
 */

import { defineStore } from 'pinia';
import router from '@/plugins/vue-router';
import * as AuthApi from '@/services/auth.api';
import * as EmployeeApi from '@/services/employee.api';
import type { IEmployee } from '@/services/employee.api';
import { ADMIN_ROLE_UUID, HOMEAPI_COMPANY_UUID } from '@/config/constants';
import {EmploymentEntity} from "@shared/interfaces";
import i18n from '@/plugins/i18n';

// --- Interfaces ---
/** @interface IAuthState The structure of the authentication state. */
export interface IAuthState {
  user: IEmployee | null;
  accessToken: string | null;
  refreshToken: string | null;
  returnUrl: string | null;
}

// Helper to get state from localStorage
const getPersistedState = (): IAuthState => {
  const persistedState = localStorage.getItem('authState');
  if (persistedState) {
    try {
      const parsed = JSON.parse(persistedState);

      // Apply persisted language if available
      if (parsed.user?.language) {
        i18n.global.locale.value = parsed.user.language;
      }

      return parsed;
    } catch (e) {
      console.error('Failed to parse auth state from localStorage', e);
    }
  }
  return { user: null, accessToken: null, refreshToken: null, returnUrl: null };
};


export const useAuthStore = defineStore('auth', {
  /**
   * Defines the initial state of the store.
   * We will later enhance this to load state from localStorage.
   *
   * @returns {IAuthState} The initial state object.
   *
   * @author Alexander Funke
   * @lastChange 2025-08-26
   */
  state: (): IAuthState => getPersistedState(),

  /**
   * Getters are computed properties for the store.
   * They are cached and only re-evaluate when their dependencies change.
   */
  getters: {
    /**
     * Checks if a user is currently authenticated.
     *
     * @param state The current state of the auth store.
     * @returns {boolean} True if a user and access token exist.
     *
     * @author Alexander Funke
     * @lastChange 2025-08-26
     */
    isAuthenticated: (state): boolean => !!state.user && !!state.accessToken,

    /**
     * Returns the full name of the user.
     *
     * @param state The current state of the auth store.
     * @returns {string} The user's full name or an empty string.
     *
     * @author Alexander Funke
     * @lastChange 2025-08-26
     */
    fullName: (state): string => {
      if (!state.user) return '';
      return `${state.user.names} ${state.user.surname}`.trim();
    },

    /**
     * Checks if the currently authenticated user is a "homeAPI Admin".
     *
     * @param state The current state of the auth store.
     * @returns {boolean} True if the user has the admin role in the homeAPI company.
     *
     * @author Alexander Funke
     * @lastChange 2025-10-09
     */
    isHomeApiAdmin: (state): boolean => {
      if (!state.user?.employments) {
        return false;
      }
      return state.user.employments.some(
        (emp: EmploymentEntity) =>
          emp.ID_companies_employer === HOMEAPI_COMPANY_UUID &&
          emp.ID_roles_role === ADMIN_ROLE_UUID
      );
    },
  },

  /**
   * Actions are methods that can be called to mutate the state.
   * They can be asynchronous and are used to commit state changes.
   */
  actions: {
    /**
     * Handles the successful login flow by storing tokens and fetching user data.
     *
     * @param data The response data from the login or 2FA verification endpoint.
     *
     * @author Alexander Funke
     * @lastChange 2025-08-26
     */
    async handleSuccessfulLogin(data: { accessToken: string, refreshToken: string }) {
      this.accessToken = data.accessToken;
      this.refreshToken = data.refreshToken;

      try {
        const userResponse = await EmployeeApi.getMyProfile();
        this.user = userResponse.data;

        // Apply user language to i18n
        if (this.user?.language) {
          i18n.global.locale.value = this.user.language as any;
        }

        localStorage.setItem('authState', JSON.stringify(this.$state));

        const targetRoute = this.returnUrl || '/dashboard';
        this.returnUrl = null;
        router.push(targetRoute);
      } catch (error) {
        console.error('Failed to fetch user profile after login:', error);
        this.logout(); // If we can't get the user, log them out.
        throw new Error('Could not retrieve user profile.');
      }
    },

    /**
     * Logs out the current user by clearing the state and localStorage.
     *
     * @author Alexander Funke
     * @lastChange 2025-08-26
     */
    logout() {
      this.$reset();
      localStorage.removeItem('authState');
      router.push('/auth/signin');
    },

    /**
     * Refreshes the access token using the refresh token.
     *
     * @returns A promise that resolves if the refresh was successful.
     *
     * @author Alexander Funke
     * @lastChange 2025-09-15
     */
    async refreshTokenAction(): Promise<void> {
      if (!this.refreshToken) {
        this.logout();
        throw new Error('No refresh token available.');
      }

      try {
        const response = await AuthApi.refreshAccessToken({ refreshToken: this.refreshToken });
        this.accessToken = response.data.accessToken;
        localStorage.setItem('authState', JSON.stringify(this.$state));
      } catch (error) {
        console.error('Token refresh failed, logging out:', error);
        this.logout();
        throw error;
      }
    },
  },
});
